import { FC, useMemo } from 'react';
import { Control, Controller, UseFormResetField, UseFormWatch } from 'react-hook-form';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import { generateJsonSchemaCompletionSource } from 'dashboard/components-lib/CodeEditor/completionSources/javascript/jsonSchemaCompletionSource';
import {
  CaseVariableDefinition,
  CaseVariableDefinitionAttributes,
} from 'dashboard/models/case-variable-definition';
import { ExternalIntegrationDataSource } from 'dashboard/models/external-integration';
import {
  LayoutVariableDefinitionType,
  ObjectQueryVariableDefinition,
  SnowflakeQueryVariableDefinition,
} from 'dashboard/models/layout-variable-definitions';
import PlatformObjectQueryEditor from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateQueryEditor/PersonaQueryEditor';
import SnowflakeQueryEditor from 'dashboard/views/DashboardCaseTemplatesShowView/CaseTemplateQueryEditor/SnowflakeQueryEditor';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { toBase64WithUnicode, toStringFromBase64WithUnicode } from 'lib/base64';
import { JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

const StyledSelect = styled(Select)`
  width: 500px;
`;

const CodeEditorWrapper = styled.div`
  height: 250px;
`;

const DEFAULT_OBJECT_QUERY: ObjectQueryVariableDefinition = {
  key: '',
  type: LayoutVariableDefinitionType.ObjectQuery,
  method_chain: 'current_case',
  json_schema: {
    type: JsonSchemaTypes.Object,
    properties: {},
  },
  transform_results: null,
};

const GenerateDefaultSnowflakeQuery = (datasourceId: string): SnowflakeQueryVariableDefinition => ({
  key: '',
  type: LayoutVariableDefinitionType.SnowflakeQuery,
  datasource_id: datasourceId,
  sql: '',
  json_schema: {
    type: JsonSchemaTypes.Object,
    properties: {},
  },
  transform_results: null,
});

interface CaseTemplateGeneralQueryEditorProps {
  control: Control<CaseVariableDefinitionAttributes, object>;
  caseVariableDefinitions: CaseVariableDefinition[];
  dataSources: ExternalIntegrationDataSource[];
  watch: UseFormWatch<CaseVariableDefinitionAttributes>;
  resetField: UseFormResetField<CaseVariableDefinitionAttributes>;
}

const CaseTemplateGeneralQueryEditor: FC<CaseTemplateGeneralQueryEditorProps> = ({
  control,
  caseVariableDefinitions,
  dataSources,
  watch,
  resetField,
}) => {
  const layoutVariableDefinitionType = watch('variable-definition.type');
  const dataSourceId = watch('variable-definition.datasource_id');
  const selectedDataSource = useMemo(
    () => dataSources.find((dataSource) => dataSource.id === dataSourceId),
    [dataSourceId, dataSources],
  );

  const handleDataSourceChange = (datasourceId: string) => {
    const newLayoutVariableDefinition =
      datasourceId === LayoutVariableDefinitionType.ObjectQuery
        ? DEFAULT_OBJECT_QUERY
        : GenerateDefaultSnowflakeQuery(datasourceId);

    resetField('variable-definition', { defaultValue: newLayoutVariableDefinition });
  };

  const schema: JsonSchemaObject = {
    type: JsonSchemaTypes.Object,
    properties: {
      ...Object.fromEntries(
        caseVariableDefinitions.map((caseVariableDefinition) => [
          caseVariableDefinition.attributes.key,
          {
            type: JsonSchemaTypes.Array,
            description: `the response from the ${caseVariableDefinition.attributes.key} query`,
          },
        ]),
      ),
    },
  };

  return (
    <>
      <div>
        <FormControlLabel htmlFor='case_template_general_query_editor--data-resource-selector'>
          Resource
        </FormControlLabel>
      </div>
      <Controller
        name='variable-definition.datasource_id'
        control={control}
        render={({ field: { value } }) => {
          return (
            <StyledSelect
              id='case_template_general_query_editor--data-resource-selector'
              onChange={(e) => handleDataSourceChange(e.target.value)}
              defaultValue={LayoutVariableDefinitionType.ObjectQuery}
              value={
                watch('variable-definition.type') === LayoutVariableDefinitionType.ObjectQuery
                  ? LayoutVariableDefinitionType.ObjectQuery
                  : value
              }
            >
              <Option value={LayoutVariableDefinitionType.ObjectQuery}>Persona Database</Option>
              {dataSources.map((dataSource) => (
                <Option key={dataSource.id} value={dataSource.id}>
                  {dataSource.attributes.name}
                </Option>
              ))}
            </StyledSelect>
          );
        }}
      />
      <Spacer height={3} />
      {layoutVariableDefinitionType === LayoutVariableDefinitionType.ObjectQuery && (
        <PlatformObjectQueryEditor control={control} />
      )}
      {layoutVariableDefinitionType === LayoutVariableDefinitionType.SnowflakeQuery && (
        <SnowflakeQueryEditor control={control} selectedDataSource={selectedDataSource} />
      )}
      <Spacer height={3} />
      <FormControlLabel id='case_template_general_query_editor--transform-results-label'>
        Transform Results
      </FormControlLabel>
      <Controller
        name='variable-definition.transform_results'
        control={control}
        render={({ field: { value, onChange } }) => (
          <CodeEditorWrapper>
            <CodeEditor
              aria-labelledby='case_template_general_query_editor--transform-results-label'
              code={value ? toStringFromBase64WithUnicode(value) : ''}
              language='javascriptTemplated'
              completionSources={{
                javascript: [generateJsonSchemaCompletionSource(schema)],
              }}
              placeholder='return data'
              editable={true}
              onChange={(newCode) => onChange(toBase64WithUnicode(newCode))}
            />
          </CodeEditorWrapper>
        )}
      />
    </>
  );
};

export default CaseTemplateGeneralQueryEditor;
